import { api } from "@/lib/apiClient";
import {
  isNachweisAuftragsart,
  isNwPlusFpAuftragsart,
} from "@/features/timesheetFlow/lib/auftragsart";

export interface ProjectCompletionDoc {
  id?: string | number | null;
  number?: string | null;
  name?: string | null;
  web_url?: string | null;
  booking_status_name?: string | null;
  hero_paid_at?: string | null;
  /** "app" | "hero" – nur App-LS zählt als fertig */
  source?: string | null;
}

export interface ProjectCompletionRow {
  id?: number;
  auftragsnummer?: string | null;
  beschreibung?: string | null;
  kunde?: string | null;
  ort?: string | null;
  ansprechpartner?: string | null;
  hero_project_id?: string | number | null;
  ordnerpfad?: string | null;
  pfad?: string | null;
  sharepoint_path?: string | null;
  sharepoint_folder_id?: string | null;
  has_folder?: boolean;
  auftragsart?: string | null;
  /** Aus App-Angebot abgeleitet (Festpreis / Nachweis / NW + FP). */
  auftragsart_app?: string | null;
  project_completed?: boolean | null;
  completion_status_key?: string | null;
  completion_status?: string | null;
  project_completion_created_at?: string | null;
  project_completion_created_by?: string | null;
  project_completion_pdf_share_url?: string | null;
  project_created_at?: string | null;
  project_closed_at?: string | null;
  customer_paid_at?: string | null;
  delivery_notes?: ProjectCompletionDoc[];
  invoices?: ProjectCompletionDoc[];
}

export type ProjectCompletionStatusKey =
  | "open"
  | "delivery_note_created"
  | "invoiced"
  | "paid"
  | "";

export type ProjectCompletionStatusFilter = "all" | ProjectCompletionStatusKey;

export const PROJECT_COMPLETION_STATUS_OPTIONS: Array<{
  value: ProjectCompletionStatusFilter;
  label: string;
}> = [
  { value: "all", label: "Alle" },
  { value: "open", label: "Offen" },
  { value: "delivery_note_created", label: "Lieferschein erstellt" },
  { value: "invoiced", label: "Abgerechnet" },
  { value: "paid", label: "Bezahlt" },
];

const COMPLETION_LABEL_BY_KEY: Record<Exclude<ProjectCompletionStatusKey, "">, string> = {
  open: "Offen",
  delivery_note_created: "Lieferschein erstellt",
  invoiced: "Abgerechnet",
  paid: "Bezahlt",
};

const COMPLETION_SORT_ORDER: Record<Exclude<ProjectCompletionStatusKey, "">, number> = {
  open: 0,
  delivery_note_created: 1,
  invoiced: 2,
  paid: 3,
};

export interface FetchProjectCompletionsParams {
  q?: string;
  start?: string;
  end?: string;
  auftragsart?: string;
  /** Auch abgeschlossene Nachweisprojekte ohne Fertigstellungsmeldung (Auswertungen). */
  includeNachweisAbschluesse?: boolean;
  limit?: number;
}

export async function fetchProjectCompletions(
  params: FetchProjectCompletionsParams | string = {},
): Promise<ProjectCompletionRow[]> {
  const options: FetchProjectCompletionsParams =
    typeof params === "string" ? { q: params } : params;
  const searchParams = new URLSearchParams();
  const term = (options.q || "").trim();
  if (term) searchParams.set("q", term);
  if (options.start) searchParams.set("start", options.start);
  if (options.end) searchParams.set("end", options.end);
  if (options.auftragsart) searchParams.set("auftragsart", options.auftragsart);
  if (options.includeNachweisAbschluesse) {
    searchParams.set("include_nachweis_abschluesse", "1");
  }
  if (options.limit) searchParams.set("limit", String(options.limit));
  const path = searchParams.toString()
    ? `/reports/project-completions?${searchParams.toString()}`
    : "/reports/project-completions";
  const data = await api.get<ProjectCompletionRow[]>(path);
  return Array.isArray(data) ? data : [];
}

function normalizeProjectCompletionStatusKey(value: unknown): ProjectCompletionStatusKey {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (normalized === "open" || normalized === "offen") return "open";
  if (normalized === "delivery_note_created" || normalized === "lieferschein_erstellt") {
    return "delivery_note_created";
  }
  if (
    normalized === "invoiced" ||
    normalized === "abgerechnet" ||
    normalized === "invoice_created"
  ) {
    return "invoiced";
  }
  if (normalized === "paid" || normalized === "bezahlt") return "paid";
  return "";
}

export function getProjectCompletionStatusMeta(row: ProjectCompletionRow): {
  key: ProjectCompletionStatusKey;
  label: string;
} {
  const normalizedFromApi = normalizeProjectCompletionStatusKey(row?.completion_status_key);
  if (normalizedFromApi) {
    return {
      key: normalizedFromApi,
      label:
        row?.completion_status ||
        COMPLETION_LABEL_BY_KEY[normalizedFromApi] ||
        "Offen",
    };
  }

  const deliveryNotes = Array.isArray(row?.delivery_notes) ? row.delivery_notes : [];
  const invoices = Array.isArray(row?.invoices) ? row.invoices : [];
  const hasPaidInvoice = invoices.some((invoice) => {
    const bookingStatus = String(invoice?.booking_status_name || "")
      .trim()
      .toLowerCase();
    return bookingStatus.includes("bezahlt") || bookingStatus === "paid";
  });
  const hasAppDelivery = deliveryNotes.some(
    (note) => String(note?.source || "").trim().toLowerCase() === "app",
  );
  if (hasPaidInvoice) return { key: "paid", label: "Bezahlt" };
  if (invoices.length > 0) return { key: "invoiced", label: "Abgerechnet" };
  if (hasAppDelivery) {
    return { key: "delivery_note_created", label: "Lieferschein erstellt" };
  }
  return { key: "open", label: "Offen" };
}

export function projectCompletionStatusBadgeClass(key: ProjectCompletionStatusKey): string {
  switch (key) {
    case "paid":
      return "bg-emerald-900 text-white";
    case "invoiced":
      return "bg-purple-600 text-white";
    case "delivery_note_created":
      return "bg-blue-600 text-white";
    case "open":
    default:
      return "bg-label-tertiary text-label";
  }
}

/** Reiner Nachweis – Abschluss läuft über Stundennachweis, nicht über diese Queue. */
export function isProjectCompletionNachweisOnly(row: ProjectCompletionRow): boolean {
  const effective = row.auftragsart_app || row.auftragsart;
  if (isNwPlusFpAuftragsart(effective)) return false;
  return isNachweisAuftragsart(effective);
}

export function isSignedProjectCompletion(row: ProjectCompletionRow): boolean {
  return Boolean(String(row.project_completion_pdf_share_url || "").trim());
}

export function filterOpenProjectCompletions(
  rows: ProjectCompletionRow[],
): ProjectCompletionRow[] {
  return rows.filter((row) => {
    if (getProjectCompletionStatusMeta(row).key !== "open") return false;
    // Offene Home-/Badge-Queue: nur echte Fertigstellungsmeldungen (PDF),
    // keine Stundennachweis-/Lieferschein-Abschlüsse.
    if (!isSignedProjectCompletion(row)) return false;
    if (isProjectCompletionNachweisOnly(row)) return false;
    return true;
  });
}

export function getProjectCompletionFolderPath(row: ProjectCompletionRow): string {
  return String(
    row.ordnerpfad || row.pfad || row.sharepoint_path || "",
  ).trim();
}

export function projectCompletionHasFolder(row: ProjectCompletionRow): boolean {
  return Boolean(
    getProjectCompletionFolderPath(row) ||
      row.sharepoint_folder_id ||
      row.has_folder,
  );
}

export function formatProjectCompletionDocList(docs: ProjectCompletionDoc[] | undefined): string {
  if (!Array.isArray(docs) || !docs.length) return "—";
  return docs
    .map((doc) => String(doc?.number || doc?.name || doc?.id || "").trim())
    .filter(Boolean)
    .join(", ");
}

export function formatProjectCompletionAuftragLabel(row: ProjectCompletionRow): string {
  const parts = [row.auftragsnummer, row.beschreibung].filter(Boolean);
  if (parts.length) return parts.join(" – ");
  return "Unbekannt";
}

export function formatProjectCompletionDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString("de-DE");
}

export interface ProjectCompletionClientFilters {
  kunde: string;
  status: ProjectCompletionStatusFilter;
}

export interface ProjectCompletionListFilters extends ProjectCompletionClientFilters {
  q: string;
}

export function filterProjectCompletions(
  rows: ProjectCompletionRow[],
  filters: ProjectCompletionListFilters,
): ProjectCompletionRow[] {
  let result = [...rows];

  if (filters.kunde) {
    const target = filters.kunde.trim().toLowerCase();
    result = result.filter((row) => String(row.kunde || "").trim().toLowerCase() === target);
  }

  if (filters.status !== "all") {
    result = result.filter(
      (row) => getProjectCompletionStatusMeta(row).key === filters.status,
    );
  }

  const term = filters.q.trim().toLowerCase();
  if (term) {
    result = result.filter((row) => {
      const haystack = [row.auftragsnummer, row.beschreibung, row.kunde, row.ort]
        .map((value) => String(value || "").toLowerCase())
        .join(" ");
      return haystack.includes(term);
    });
  }

  return result;
}

export function sortProjectCompletions(rows: ProjectCompletionRow[]): ProjectCompletionRow[] {
  return [...rows].sort((a, b) => {
    const keyA = getProjectCompletionStatusMeta(a).key || "open";
    const keyB = getProjectCompletionStatusMeta(b).key || "open";
    const groupDiff =
      (COMPLETION_SORT_ORDER[keyA as Exclude<ProjectCompletionStatusKey, "">] ?? 99) -
      (COMPLETION_SORT_ORDER[keyB as Exclude<ProjectCompletionStatusKey, "">] ?? 99);
    if (groupDiff !== 0) return groupDiff;

    const referenceA = getProjectCompletionReferenceDate(a);
    const referenceB = getProjectCompletionReferenceDate(b);
    const refMsA = referenceA ? new Date(referenceA).getTime() : 0;
    const refMsB = referenceB ? new Date(referenceB).getTime() : 0;
    return refMsB - refMsA;
  });
}

export function getProjectCompletionPaidAt(row: ProjectCompletionRow): string | null {
  if (row.customer_paid_at) return row.customer_paid_at;
  const invoices = Array.isArray(row.invoices) ? row.invoices : [];
  let latest: string | null = null;
  for (const invoice of invoices) {
    const bookingStatus = String(invoice?.booking_status_name || "")
      .trim()
      .toLowerCase();
    if (!bookingStatus.includes("bezahlt") && bookingStatus !== "paid") continue;
    const paidAt = invoice?.hero_paid_at;
    if (!paidAt) continue;
    if (!latest || new Date(paidAt).getTime() > new Date(latest).getTime()) {
      latest = paidAt;
    }
  }
  return latest;
}

export function getProjectCompletionClosedAt(row: ProjectCompletionRow): string | null {
  if (row.project_completion_created_at) return row.project_completion_created_at;
  if (row.project_closed_at) return row.project_closed_at;
  return null;
}

export function getProjectCompletionReferenceDate(row: ProjectCompletionRow): string | null {
  return getProjectCompletionPaidAt(row) ?? getProjectCompletionClosedAt(row);
}

export function listProjectCompletionKunden(rows: ProjectCompletionRow[]): string[] {
  return [
    ...new Set(rows.map((row) => String(row.kunde || "").trim()).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b, "de"));
}

export function projectCompletionFiltersActive(
  filters: ProjectCompletionListFilters,
): boolean {
  return Boolean(filters.q.trim() || filters.kunde || filters.status !== "all");
}

export interface ProjectCompletionStats {
  total: number;
  byStatus: Partial<Record<ProjectCompletionStatusKey, number>>;
  avgLeadTimeDays: number | null;
}

export function computeProjectCompletionLeadTimeDays(
  row: ProjectCompletionRow,
): number | null {
  const start = row.project_created_at;
  const end = getProjectCompletionPaidAt(row);
  if (!start || !end) return null;
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) return null;
  return Math.round((endMs - startMs) / (1000 * 60 * 60 * 24));
}

export function formatLeadTimeDays(days: number | null | undefined): string {
  if (!Number.isFinite(days)) return "–";
  const value = Math.max(0, Math.round(Number(days)));
  return value === 1 ? "1 Tag" : `${value} Tage`;
}

export function computeProjectCompletionStats(
  rows: ProjectCompletionRow[],
): ProjectCompletionStats {
  const byStatus: Partial<Record<ProjectCompletionStatusKey, number>> = {};
  const leadTimes: number[] = [];

  for (const row of rows) {
    const key = getProjectCompletionStatusMeta(row).key || "open";
    byStatus[key] = (byStatus[key] ?? 0) + 1;
    const leadTime = computeProjectCompletionLeadTimeDays(row);
    if (leadTime != null) leadTimes.push(leadTime);
  }

  const avgLeadTimeDays =
    leadTimes.length > 0
      ? Math.round(leadTimes.reduce((sum, value) => sum + value, 0) / leadTimes.length)
      : null;

  return {
    total: rows.length,
    byStatus,
    avgLeadTimeDays,
  };
}
